import { useCallback, useMemo } from 'react';
import { useProviders } from './use-providers';
import { useLocations } from './use-locations';
import { useEntities } from './use-entities';
import { useLabels } from './use-labels';

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export type ConditionValueResolver = (field: string, raw: string) => string;

/**
 * Resolve a stored assignment-rule condition value (UUID for
 * provider/location/insurance, slug for label, code for priority) to
 * its user-facing display name.
 *
 * Single source of truth shared between the rules-table summary
 * (`RuleSummary.tsx`) and the editor's live-preview sentence
 * (`condition-builder.tsx`). Array formatting (joining, truncation,
 * placeholders) is intentionally NOT in here — each caller renders
 * differently (badges vs sentence) and shares only the per-value
 * resolution.
 *
 * Falls back to the raw value when the entity has been deleted /
 * disabled or the catalog hasn't loaded yet — the cell renders the
 * stored slug rather than an empty string. Honest > pretty.
 */
export function useConditionValueResolver(): ConditionValueResolver {
  const { data: providersData } = useProviders();
  const { data: locationsData } = useLocations();
  // Pragmatic ceiling on the payer catalog. The resolver renders the
  // raw UUID for any payer beyond this limit (per the documented
  // fallback below), which is honest but ugly. 1000 covers realistic
  // org sizes; the proper fix when this proves insufficient is to
  // (a) accept the conditions list as a hook parameter, (b) extract
  // the unique UUIDs referenced across all conditions, (c) fetch only
  // those via a batch-by-ids endpoint (which doesn't yet exist on the
  // entity client). Tracked as a follow-up — current fetch is wasteful
  // for a 9-rule org but not broken.
  const { data: entitiesData } = useEntities({ type: 'payer', limit: 1000 });
  const { data: labelsData } = useLabels();

  // Map<field, Map<id-or-slug, displayName>> — natural for "lookup
  // value by key, miss returns undefined" without object-as-record
  // type gymnastics. `maps.get(field)?.get(raw)` chains cleanly.
  const maps = useMemo(() => {
    const m = new Map<string, Map<string, string>>();
    m.set(
      'provider',
      new Map(
        (providersData?.items ?? [])
          .filter((p) => p.rowStatus === 0)
          .map((p) => [p.id, `${p.firstName} ${p.lastName}`])
      )
    );
    m.set(
      'location',
      new Map(
        (locationsData?.items ?? []).filter((l) => l.rowStatus === 0).map((l) => [l.id, l.name])
      )
    );
    m.set(
      'insurance',
      new Map(
        (entitiesData?.items ?? []).filter((e) => e.rowStatus === 0).map((e) => [e.id, e.name])
      )
    );
    m.set(
      'label',
      new Map(
        (labelsData ?? [])
          .filter((l) => l.enabled && l.rowStatus === 0)
          .map((l) => [l.name, l.displayName])
      )
    );
    return m;
  }, [providersData?.items, locationsData?.items, entitiesData?.items, labelsData]);

  return useCallback(
    (field, raw) => {
      if (field === 'priority') return PRIORITY_LABELS[raw] ?? raw;
      return maps.get(field)?.get(raw) ?? raw;
    },
    [maps]
  );
}
